export type ImageModelId = 'gemini-3.1-flash-image' | 'gemini-3-pro-image';

export type TextModelId = 'gemini-3.8-flash' | 'gemini-3.1-pro-preview';

export type VideoModelId = 'veo-3.1' | 'veo-3.1-fast' | 'veo-3' | 'veo-3-fast';

export type ModelId = ImageModelId | TextModelId | VideoModelId;

// Image model ids use the GA (un-suffixed) publisher form. This is the ONLY
// form Vertex Express serves — the `-preview`-suffixed ids are Developer-API
// shape and 404 on the Express surface used by server/api/generate-image.post.ts
// (incident 2026-07-21). The GA ids also serve on the Developer API, so a single
// SOT works across both surfaces (probed 200 on both; Flash GA generates live on
// Express). Model identity is unchanged — Nano Banana 2 / Nano Banana Pro.
export const IMAGE_MODELS = {
  FLASH: 'gemini-3.1-flash-image' as const,
  PRO: 'gemini-3-pro-image' as const,
};

export const TEXT_MODELS = {
  // PRO's `-preview` suffix is a DELIBERATE, VERIFIED exception to the
  // no-`-preview` rule below — that rule is about the IMAGE surface only
  // (server/api/generate-image.post.ts's Vertex Express image endpoint,
  // incident 2026-07-21). Text is a different Vertex Express endpoint and
  // does NOT reject `-preview` ids. Live-probed 2026-08-24 (audit for this
  // session's model-ID sweep) with the exact client every text surface uses
  // (`createVertex({apiKey}).../edge`, src/lib/ai-agent/vertex-text.ts):
  // real "OK" response, finishReason 'stop', no 404. Re-verify if this ever
  // starts erroring — do not re-flag it as a bug from the id string alone.
  //
  // Migrated 2026-09-16, gemini-3.7-flash -> gemini-3.8-flash. Verified two
  // ways, not from the pasted/summarized doc alone: (1) a direct fetch of
  // https://ai.google.dev/gemini-api/docs/models/gemini-3.8-flash and
  // https://ai.google.dev/gemini-api/docs/pricing, both live; (2) a real
  // `generateContent` smoke test against the direct Gemini Developer API —
  // 200, finishReason 'STOP', `modelVersion: "gemini-3.8-flash"`. Pricing is
  // IDENTICAL to 3.7-flash through 2026-12-31 — $0.75/$3.75 per 1M in/out
  // tokens, $0.075/1M cached-input-read — then both step to $1.50/$7.50/$0.15
  // on 2027-01-01 per Google's own pricing page (an already-announced
  // schedule, not a 3.8-specific increase). Same 1,048,576 context window and
  // 65,536 max output tokens as 3.7. 3.8-flash's own docs describe it as
  // "our most intelligent Flash model, engineered for long-horizon software
  // engineering, autonomous agents, and complex enterprise workflows" with
  // published gains over 3.7 on Terminal-bench 2.1 (90.8% vs 81.6%), SWE-Bench
  // Pro, and SWE-Atlas.
  //
  // ⚠️ BREAKING for thinking level: 3.8-flash's docs state `MINIMAL` returns
  // an API validation error ("Thinking level MINIMAL is not supported for
  // this model") — reproduced live with a direct 400 on this exact model id.
  // 3.7-flash accepted MINIMAL, and four call sites relied on it for
  // cheap/fast extraction (style-extraction/extractStyle.ts,
  // server/api/looks/derive.post.ts, ai-agent/preflight/anchor-quality.ts,
  // server/api/engagement/suggest-reply.post.ts) — all migrated to `'low'`,
  // the new floor, in the same change. The user-facing composer slider
  // (ComposerThinkingMenu, thinking-policy.ts's `ThinkingLevel` type) was
  // already restricted to `low | medium | high` and needed no change.
  // Prior migration: docs/superpowers/specs/2026-07-24-gemini-3.6-flash-migration-design.md.
  FLASH: 'gemini-3.8-flash' as const,
  PRO: 'gemini-3.1-pro-preview' as const,
};

/**
 * Input context window, in tokens, per TEXT_MODELS entry. Named beside
 * TEXT_MODELS itself (rather than left as a magic number in the pre-flight
 * budget check) so the window and the model id it belongs to can never drift
 * apart independently.
 *
 * FLASH: gemini-3.8-flash's published "1M-token" context window, expressed as
 * the exact 2^20 figure (1,048,576) Google's model docs use for "1M" context
 * models. This is the live chat agent's model (BACKEND_MODELS.CHAT_DEFAULT ===
 * TEXT_MODELS.FLASH) — see src/lib/ai-agent/context-budget.ts, the only
 * consumer, for the pre-flight size check this constant exists to support.
 */
export const TEXT_MODEL_CONTEXT_WINDOW = {
  FLASH: 1_048_576,
} as const;

export const VIDEO_MODELS = {
  VEO_31: 'veo-3.1' as const,
  VEO_31_FAST: 'veo-3.1-fast' as const,
  VEO_3: 'veo-3' as const,
  VEO_3_FAST: 'veo-3-fast' as const,
};

export const BACKEND_MODELS = {
  // Kept in sync with TEXT_MODELS.FLASH — see the migration note above.
  CHAT_DEFAULT: 'gemini-3.8-flash' as const,
  // GA (un-suffixed) flash-lite. Serves on BOTH the Developer API and Vertex
  // (probed 200 on both; generateContent 200 live on the Developer API) and
  // aligns with the founder's 3.5+ directive. The old `gemini-3.1-flash-lite-preview`
  // did not exist on Vertex at all (the 2026-07-17 Discover-AI outage).
  FLASH_LITE: 'gemini-3.5-flash-lite' as const,
};

export const EMBEDDING_MODELS = {
  GOOGLE_DEFAULT: 'google/gemini-embedding-001' as const,
};

export const GATEWAY_SLUGS = {
  FLASH: `google/${TEXT_MODELS.FLASH}` as const,
  PRO: `google/${TEXT_MODELS.PRO}` as const,
  CHAT_DEFAULT: `google/${BACKEND_MODELS.CHAT_DEFAULT}` as const,
  FLASH_IMAGE: `google/${IMAGE_MODELS.FLASH}` as const,
  // Gateway image slugs are just the `google/`-prefixed GA ids. Now that
  // IMAGE_MODELS pins the un-suffixed GA form (see the IMAGE_MODELS note),
  // the gateway and direct-SDK ids no longer drift — both are suffix-free.
  PRO_IMAGE: `google/${IMAGE_MODELS.PRO}` as const,
  FLASH_LITE: `google/${BACKEND_MODELS.FLASH_LITE}` as const,
  SONAR: 'perplexity/sonar' as const,
  SONAR_PRO: 'perplexity/sonar-pro' as const,
} as const;

/**
 * User-visible model names. These are PROVENANCE / Advanced-picker labels
 * (e.g. "generated by Gemini 3 Pro Image"), NOT the composer tier abstraction
 * — the composer's Auto/Standard/Mora Pro tiers live in `MODE_MODEL_TIERS`
 * (src/components/create/v2/CreativeChat.tsx). The image entries were
 * de-codenamed (2026-06-21): "Nano Banana 2/Pro" is Google's internal
 * codename and leaked into the UI. Internal routing, hard rules, and
 * LLM-facing tool prompts still use the canonical "Nano Banana Pro" name.
 */
export const MODEL_DISPLAY_NAMES: Record<string, string> = {
  [IMAGE_MODELS.FLASH]: 'Gemini 3.1 Flash Image',
  [IMAGE_MODELS.PRO]: 'Gemini 3 Pro Image',
  [TEXT_MODELS.FLASH]: 'Gemini 3.8 Flash',
  [TEXT_MODELS.PRO]: 'Gemini 3.1 Pro',
  [VIDEO_MODELS.VEO_31]: 'Veo 3.1',
  [VIDEO_MODELS.VEO_31_FAST]: 'Veo 3.1 Fast',
  [VIDEO_MODELS.VEO_3]: 'Veo 3',
  [VIDEO_MODELS.VEO_3_FAST]: 'Veo 3 Fast',
};

/** Second reader for the ad claims check, from a different family than the Gemini writer. */
export const AD_CLAIMS_CHECK_MODEL = 'claude-sonnet-5-5' as const;
